import {useEffect, useMemo, useState} from "react";
import {useMutation, useQuery} from "@tanstack/react-query";
import {ArrowLeft, Search, Split} from "lucide-react";
import {Link, useNavigate} from "react-router-dom";
import {getAuthToken} from "../../shared/api/client";
import {useCapabilities} from "../../shared/auth/CapabilitiesContext";
import {DataState} from "../../shared/components/DataState";
import {FinancialCommandDialog} from "../../shared/components/FinancialCommandDialog";
import {
    addMoney,
    compareMoney,
    formatExactMoney,
    normalizeMoney,
} from "../../shared/finance/decimal";
import {mapFinanceError} from "../../shared/finance/errors";
import {
    createSplitBill,
    listSplitBillStaff,
    MAX_SPLIT_BILL_AMOUNT,
    searchSplitBillBranches,
} from "./api";
import {useSplitCountryScope} from "./useSplitCountryScope";
import {useTranslation} from "../../shared/i18n";
import "./splitBills.css";

const scopedBranches = (places) =>
    (Array.isArray(places) ? places : []).flatMap((place) =>
        (Array.isArray(place.branches) ? place.branches : []).map((branch) => ({
            id: branch.id,
            name: branch.name,
            label: `${place.name} · ${branch.name}`,
        })),
    );

const positiveInteger = (value, invalidMessage) => {
    const text = String(value ?? "").trim();
    if (!/^\d+$/.test(text)) throw new Error(invalidMessage);
    return Number(text);
};

export const createSplitIdempotencyKey = () => {
    if (typeof window.crypto?.randomUUID === "function") {
        return `split-${window.crypto.randomUUID()}`;
    }
    return `split-${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
};

export const SplitBillCreatePage = () => {
    const {t} = useTranslation();
    const navigate = useNavigate();
    const capabilities = useCapabilities();
    const {countryScope, activeCountryScopeRef, scopeBlock} = useSplitCountryScope(capabilities);
    const branches = useMemo(
        () => scopedBranches(capabilities.scope?.places),
        [capabilities.scope?.places],
    );
    const [searchText, setSearchText] = useState("");
    const [foundBranches, setFoundBranches] = useState([]);
    const [branchId, setBranchId] = useState("");
    const [personalId, setPersonalId] = useState("");
    const [amount, setAmount] = useState("");
    const [partsCount, setPartsCount] = useState("2");
    const [expiresInMinutes, setExpiresInMinutes] = useState("");
    const [validationError, setValidationError] = useState("");
    const [confirmation, setConfirmation] = useState(null);
    const [mutationError, setMutationError] = useState(null);

    useEffect(() => {
        setFoundBranches([]);
        setBranchId("");
        setPersonalId("");
        setConfirmation(null);
        setValidationError("");
        setMutationError(null);
    }, [countryScope]);

    const searchMutation = useMutation({
        mutationFn: ({requestScope, query}) =>
            searchSplitBillBranches({
                countryScope: requestScope,
                actorCountryId: capabilities.country?.id,
                query,
            }),
        onSuccess: (result, variables) => {
            if (activeCountryScopeRef.current !== variables.requestScope) return;
            setFoundBranches(result.map((branch) => ({...branch, label: branch.name})));
            setValidationError("");
        },
        onError: (error, variables) => {
            if (activeCountryScopeRef.current !== variables.requestScope) return;
            setValidationError(mapFinanceError(error).message);
        },
    });

    const visibleBranches = branches.length ? branches : foundBranches;
    const staffQuery = useQuery({
        queryKey: ["split-bill-staff", String(countryScope), String(branchId)],
        queryFn: () => listSplitBillStaff({
            countryScope,
            actorCountryId: capabilities.country?.id,
            branchId: Number(branchId),
        }),
        enabled: scopeBlock === null && Boolean(branchId),
        retry: false,
    });

    const createMutation = useMutation({
        mutationFn: ({record, requestScope}) => createSplitBill({
            countryScope: requestScope,
            actorCountryId: capabilities.country?.id,
            amount: record.amount,
            partsCount: record.partsCount,
            branchId: record.branchId,
            personalId: record.personalId,
            expiresInMinutes: record.expiresInMinutes,
            idempotencyKey: record.idempotencyKey,
        }),
        onSuccess: (snapshot, variables) => {
            if (
                getAuthToken() !== variables.requestSession ||
                activeCountryScopeRef.current !== variables.requestScope
            ) return;
            navigate(`/finance/split-bills/${snapshot.group.id}`);
        },
        onError: (error, variables) => {
            if (
                getAuthToken() !== variables.requestSession ||
                activeCountryScopeRef.current !== variables.requestScope
            ) return;
            setMutationError(mapFinanceError(error));
        },
    });

    if (!capabilities.hasAction("split_bills.create")) {
        return <DataState title={t("money.splitBills.createPage.unavailableTitle")} copy={t("money.splitBills.createPage.unavailableCopy")} />;
    }

    if (scopeBlock) {
        return (
            <div className="admin-page split-create-page">
                <Link className="split-back-link" to="/finance/split-bills">
                    <ArrowLeft aria-hidden="true" size={16} /> {t("money.splitBills.createPage.back")}
                </Link>
                <DataState title={scopeBlock.title} copy={scopeBlock.copy} />
            </div>
        );
    }

    const selectedBranch = visibleBranches.find(({id}) => String(id) === branchId);
    const selectedPersonal = (staffQuery.data || []).find(({id}) => String(id) === personalId);

    const prepareConfirmation = (event) => {
        event.preventDefault();
        try {
            const normalizedAmount = normalizeMoney(amount);
            if (normalizedAmount === "0.00") throw new Error(t("money.splitBills.createPage.errors.amountPositive"));
            if (compareMoney(normalizedAmount, MAX_SPLIT_BILL_AMOUNT) > 0) {
                throw new Error(t("money.splitBills.createPage.errors.amountMax", {max: MAX_SPLIT_BILL_AMOUNT}));
            }
            const normalizedParts = positiveInteger(partsCount, t("money.splitBills.createPage.errors.integer", {label: t("money.splitBills.createPage.parts")}));
            if (normalizedParts < 2 || normalizedParts > 20) {
                throw new Error(t("money.splitBills.createPage.errors.partsRange"));
            }
            const minimumAmount = addMoney(Array.from({length: normalizedParts}, () => "0.01"));
            if (compareMoney(normalizedAmount, minimumAmount) < 0) {
                throw new Error(t("money.splitBills.createPage.errors.partMinimum"));
            }
            if (!selectedBranch) throw new Error(t("money.splitBills.createPage.errors.chooseBranch"));
            const normalizedExpiry = expiresInMinutes === ""
                ? undefined
                : positiveInteger(expiresInMinutes, t("money.splitBills.createPage.errors.integer", {label: t("money.splitBills.createPage.expiryField")}));
            if (normalizedExpiry !== undefined && (normalizedExpiry < 1 || normalizedExpiry > 1440)) {
                throw new Error(t("money.splitBills.createPage.errors.expiryRange"));
            }
            setValidationError("");
            setMutationError(null);
            setConfirmation({
                amount: normalizedAmount,
                partsCount: normalizedParts,
                branchId: Number(selectedBranch.id),
                branchName: selectedBranch.name,
                personalId: selectedPersonal ? Number(selectedPersonal.id) : undefined,
                personalName: selectedPersonal?.name || t("money.splitBills.createPage.noStaff"),
                expiresInMinutes: normalizedExpiry,
                idempotencyKey: createSplitIdempotencyKey(),
            });
        } catch (error) {
            setValidationError(error.message);
        }
    };

    return (
        <div className="admin-page split-create-page">
            <Link className="split-back-link" to="/finance/split-bills">
                <ArrowLeft aria-hidden="true" size={16} /> {t("money.splitBills.createPage.back")}
            </Link>
            <header className="admin-page-header">
                <div>
                    <p className="admin-eyebrow">{t("money.splitBills.createPage.eyebrow")}</p>
                    <h1 className="admin-page-title">{t("money.splitBills.createPage.title")}</h1>
                    <p className="admin-page-copy">
                        {t("money.splitBills.createPage.copy")}
                    </p>
                </div>
            </header>

            <form className="split-create-form" onSubmit={prepareConfirmation}>
                <section className="split-form-section" aria-labelledby="split-context-title">
                    <div>
                        <h2 id="split-context-title">{t("money.splitBills.createPage.contextTitle")}</h2>
                        <p>{t("money.splitBills.createPage.contextNote")}</p>
                    </div>
                    <div className="split-form-fields">
                        {!branches.length ? (
                            <div className="split-branch-search">
                                <label className="admin-field">
                                    <span>{t("money.splitBills.createPage.search")}</span>
                                    <input
                                        type="search"
                                        aria-label={t("money.splitBills.createPage.search")}
                                        value={searchText}
                                        onChange={(event) => setSearchText(event.target.value)}
                                    />
                                </label>
                                <button
                                    className="admin-button admin-button-secondary"
                                    type="button"
                                    disabled={searchMutation.isPending}
                                    onClick={() => searchMutation.mutate({
                                        requestScope: countryScope,
                                        query: searchText,
                                    })}
                                >
                                    <Search aria-hidden="true" size={16} /> {t("money.splitBills.createPage.find")}
                                </button>
                            </div>
                        ) : null}
                        <label className="admin-field">
                            <span>{t("money.splitBills.createPage.branch")}</span>
                            <select
                                aria-label={t("money.splitBills.createPage.branch")}
                                value={branchId}
                                onChange={(event) => {
                                    setBranchId(event.target.value);
                                    setPersonalId("");
                                }}
                            >
                                <option value="">{t("money.splitBills.createPage.chooseBranch")}</option>
                                {visibleBranches.map((branch) => (
                                    <option key={branch.id} value={String(branch.id)}>{branch.label}</option>
                                ))}
                            </select>
                        </label>
                        <label className="admin-field">
                            <span>{t("money.splitBills.createPage.staff")}</span>
                            <select
                                aria-label={t("money.splitBills.createPage.staff")}
                                value={personalId}
                                disabled={!branchId || staffQuery.isLoading}
                                onChange={(event) => setPersonalId(event.target.value)}
                            >
                                <option value="">{t("money.splitBills.createPage.noStaff")}</option>
                                {(staffQuery.data || []).map((staff) => (
                                    <option key={staff.id} value={String(staff.id)}>{staff.name}</option>
                                ))}
                            </select>
                        </label>
                    </div>
                </section>

                <section className="split-form-section" aria-labelledby="split-amount-title">
                    <div>
                        <h2 id="split-amount-title">{t("money.splitBills.createPage.paramsTitle")}</h2>
                        <p>{t("money.splitBills.createPage.paramsNote")}</p>
                    </div>
                    <div className="split-form-fields split-form-money-fields">
                        <label className="admin-field">
                            <span>{t("money.splitBills.createPage.amount")}</span>
                            <input
                                aria-label={t("money.splitBills.createPage.amount")}
                                type="text"
                                inputMode="decimal"
                                value={amount}
                                onChange={(event) => setAmount(event.target.value)}
                            />
                        </label>
                        <label className="admin-field">
                            <span>{t("money.splitBills.createPage.parts")}</span>
                            <input
                                aria-label={t("money.splitBills.createPage.parts")}
                                type="number"
                                min="2"
                                max="20"
                                step="1"
                                value={partsCount}
                                onChange={(event) => setPartsCount(event.target.value)}
                            />
                        </label>
                        <label className="admin-field">
                            <span>{t("money.splitBills.createPage.expiry")}</span>
                            <input
                                aria-label={t("money.splitBills.createPage.expiry")}
                                type="number"
                                min="1"
                                max="1440"
                                step="1"
                                value={expiresInMinutes}
                                onChange={(event) => setExpiresInMinutes(event.target.value)}
                            />
                        </label>
                    </div>
                </section>

                {validationError ? <p className="admin-form-error" role="alert">{validationError}</p> : null}
                {staffQuery.isError ? (
                    <p className="admin-form-error" role="alert">
                        {mapFinanceError(staffQuery.error).message}
                    </p>
                ) : null}
                <div className="split-form-actions">
                    <button className="admin-button admin-button-primary" type="submit">
                        <Split aria-hidden="true" size={16} /> {t("money.splitBills.createPage.submit")}
                    </button>
                </div>
            </form>

            <FinancialCommandDialog
                open={Boolean(confirmation)}
                dialogKey={confirmation ? `${confirmation.branchId}:${confirmation.amount}:${confirmation.partsCount}` : "split-create"}
                title={t("money.splitBills.createPage.confirmTitle")}
                description={t("money.splitBills.createPage.confirmDescription")}
                entity={t("money.splitBills.createPage.entity")}
                details={confirmation ? [
                    {label: t("money.splitBills.createPage.confirmAmount"), value: formatExactMoney(confirmation.amount)},
                    {label: t("money.splitBills.createPage.confirmParts"), value: String(confirmation.partsCount)},
                    {label: t("money.splitBills.createPage.confirmBranch"), value: confirmation.branchName},
                    {label: t("money.splitBills.createPage.confirmStaff"), value: confirmation.personalName},
                    {label: t("money.splitBills.createPage.confirmExpiry"), value: confirmation.expiresInMinutes ? t("money.splitBills.createPage.minutes", {count: confirmation.expiresInMinutes}) : t("money.splitBills.createPage.defaultExpiry")},
                ] : []}
                pending={createMutation.isPending}
                error={mutationError}
                confirmLabel={t("money.splitBills.createPage.confirm")}
                onCancel={() => {
                    if (createMutation.isPending) return;
                    setConfirmation(null);
                    setMutationError(null);
                }}
                onConfirm={() => createMutation.mutate({
                    record: confirmation,
                    requestScope: countryScope,
                    requestSession: getAuthToken(),
                })}
            />
        </div>
    );
};
